import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table, Button, Modal, Tooltip, SelectTamanoPagina } from 'components';
import Eliminar from 'assets/images/DeleteForever/delete_forever.svg';
import Editar from 'assets/images/Edit/edit_icon.svg';
import './TablaCatalogo.scss';
import { Col } from 'antd';
import { MenuFoldOutlined } from '@ant-design/icons';
import { filterList } from 'utils/filterList';
import { generateId } from 'utils/idGenerator';
import { base64toBlob } from 'utils/file';
import { DATE_FORMAT_FILTER_COLUMN, DATE_FORMAT_SHORT } from 'utils/date';
import dayjs from 'dayjs';
import { downloadExcelCatFechas } from 'actions/files.actions';

import { PROCESOS_ESPECIFICOS } from 'constants/nombresCatalogos';
import { selectCatalogByName } from 'selectors/catalogos.selectors';
import { selectCatFechas } from 'selectors/catFechas.selector';
import { updateCatFechas, getCatFechas } from 'actions/catFechas.actions';

import confirmarEliminar from '../../../../../assets/images/eliminar_monitoreo.jpg';

import ModalFormCatFechas from '../ModalFormCatFechas/ModalFormCatFechas';

const TablaCatalogo = ({ mainFilters }) => {
  const dispatch = useDispatch();
  const [filters] = useState({});

  const procesosEspe = useSelector(selectCatalogByName(PROCESOS_ESPECIFICOS));

  const { loading, data } = useSelector(selectCatFechas);
  const [descProceso, setDescProceso] = useState(null);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isOpenEliminar, setIsOpenEliminar] = useState(false);
  const [isOpenEliminarNoPermitido, setIsOpenEliminarNoPermitido] = useState(false);
  const [isOpenShowDates, setIsOpenShowDates] = useState(false);
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(false);
  const [periodosRegistrados, setPeriodosRegistrados] = useState([]);

  const catalogosStore = {};
  catalogosStore.procesosEspe = procesosEspe;

  const showTotal = (total, range) => {
    return `Mostrando del ${range[0]} al ${range[1]} de ${total} registros`;
  };

  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'small',
    pageSize: 10,
    current: 1,
    showTotal,
    total: 0,
    showSizeChanger: false
  });

  const handleChangePageSize = (pageSize) => {
    setPagination((prevPagination) => ({ ...prevPagination, pageSize }));
  };

  const handleOpenModal = () => {
    setIsFormModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsFormModalVisible(false);
    setRegistroSeleccionado(null);
  };

  // Se filtran los resultados buscados
  const filteredList = useMemo(() => {
    let filteredData = [];
    if (data && data.length > 0) {
      setDescProceso(data[0].descripcion);
      filteredData = data[0].periodos;

      // Se agrega descripción del proceso electoral como solicita el back para generar el excel sin consultar la BD de nuevo
      for (let index = 0; index < filteredData.length; index += 1) {
        filteredData[index].procesoElectoral = data[0].descripcion;
      }
      filteredData.sort((a, b) => b.idProcesosPeriodo - a.idProcesosPeriodo);
    }
    return filteredData;
  }, [data, mainFilters]);

  const dataList = useMemo(() => {
    return filterList(filteredList, filters);
  }, [filteredList, filters]);

  const cancelDelete = () => {
    setIsOpenEliminar(false);
    setRegistroSeleccionado(null);
  };

  const confirmDelete = async () => {
    const peticion = {
      toCancel: [
        {
          idProcesosPeriodo: registroSeleccionado.idProcesosPeriodo
        }
      ]
    };
    const { success } = await dispatch(updateCatFechas(peticion));
    if (success) {
      setConfirmEliminar(true);
      setIsOpenEliminar(false);
      setRegistroSeleccionado(null);
    }
  };
  const handleCerrarSuccess = () => {
    setConfirmEliminar(false);
  };
  const cancelShowDates = () => {
    setIsOpenShowDates(false);
    setRegistroSeleccionado(null);
  };

  const getPeriodosProceso = async (idProcesoElectoral) => {
    // Se consultan los periodos registrados en el proceso seleccionado
    // para validar que no se registren periodos duplicados o encimados
    let periodos = [];
    const { success, response } = await dispatch(
      getCatFechas(
        {
          idProcesoElectoral
        },
        false
      )
    );
    if (success && response?.data.code === 200) {
      const periodosBD = response.data.procesosElectorales[0];
      periodos = periodosBD.periodos;
    }
    return periodos;
  };

  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const { success, response } = await dispatch(downloadExcelCatFechas({ periodos: dataList }));
      if (response !== null && response !== undefined && success) {
        if (response.data.code === 200) {
          if (response.data.excel64) {
            const url = window.URL.createObjectURL(
              base64toBlob(response.data.excel64.base64, response.data.excel64.tipo)
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', response.data.excel64.nombre);
            link.click();
          }
        }
      }
      setDownloadingExcel(false);
    } catch (error) {
      setDownloadingExcel(false);
    }
  };

  dayjs.locale('es');
  const construirFechaFromString = (fecha) => {
    if (fecha !== '' && fecha) {
      return dayjs(fecha, 'YYYYMMDDHHmmss').format(DATE_FORMAT_FILTER_COLUMN);
    }
    return '';
  };
  const construirPeriodo = (inicio, fin, mayus) => {
    if (inicio && fin) {
      let resultDate;
      if (inicio.substring(0, 4) === fin.substring(0, 4)) {
        // El año es el mismo en ambas fechas
        const firstDate = construirFechaFromString(inicio);
        const secondDate = construirFechaFromString(fin);
        resultDate = `DEL ${firstDate.substring(0, firstDate.length - 8)} AL ${secondDate}`;
      } else {
        // Los años son diferentes
        resultDate = `DEL ${construirFechaFromString(inicio)} AL ${construirFechaFromString(fin)}`;
      }

      if (mayus) {
        return resultDate.toUpperCase();
      }
      return resultDate;
    }
    return '';
  };
  const construirPeriodoCorto = (date) => {
    if (date) {
      return `${dayjs(date, 'YYYYMMDDHHmmss').format(DATE_FORMAT_SHORT)}`.toUpperCase();
    }
    return '';
  };

  const handleDeleteMonitoring = (selected) => {
    if (selected.fechas.some((f) => f.asignado === 1)) {
      // No se puede eliminar porque ya está siendo usado por algún monitoreo
      setIsOpenEliminarNoPermitido(true);
      setRegistroSeleccionado(selected);
    } else {
      setIsOpenEliminar(true);
      setRegistroSeleccionado(selected);
    }
  };
  const handleEditMonitoring = async (selected) => {
    const periodosProceso = await getPeriodosProceso(selected.idProcesoElectoral);
    setPeriodosRegistrados(periodosProceso);
    setRegistroSeleccionado(selected);
    setIsFormModalVisible(true);
  };

  const handleShowDates = (selected) => {
    setIsOpenShowDates(true);
    setRegistroSeleccionado(selected);
  };

  const renderDescProceso = () => descProceso;
  const renderPeriodos = (_, row) => (
    <div className="desc-actions">
      <Col xs={19}>{construirPeriodo(row.fechaInicio, row.fechaFin, true)}</Col>
      <Col xs={1}>
        <Tooltip
          title="Ver periodos"
          className="tooltip-acciones btn-deleteoutlined"
          placement="topLeft">
          <Button
            shape="circle"
            className="btnColumnAccion"
            icon={<MenuFoldOutlined style={{ color: '#cf4e91' }} />}
            onClick={() => {
              handleShowDates(row);
            }}
          />
        </Tooltip>
      </Col>
    </div>
  );
  const renderActions = (_, row) => (
    <div className="actions-container">
      <Tooltip title="Editar" className="tooltip-acciones btn-deleteoutlined" placement="topLeft">
        <Button
          shape="circle"
          className="btnTblAccion"
          icon={<img src={Editar} alt="Editar" />}
          onClick={() => {
            handleEditMonitoring(row);
            handleOpenModal();
          }}
          ghost
        />
      </Tooltip>
      <Tooltip title="Eliminar" className="tooltip-acciones btn-deleteoutlined" placement="topLeft">
        <Button
          shape="circle"
          className="btnTblAccion"
          icon={<img src={Eliminar} alt="Eliminar" />}
          onClick={() => {
            handleDeleteMonitoring(row);
          }}
        />
      </Tooltip>
    </div>
  );

  const columns = [
    {
      title: 'ID del catálogo',
      key: 'idProcesosPeriodo',
      dataIndex: 'idProcesosPeriodo',
      width: '15%',
      align: 'center'
    },
    {
      title: 'Proceso Electoral',
      key: 'descripcion',
      dataIndex: 'descripcion',
      width: '35%',
      render: renderDescProceso
    },
    {
      title: 'Periodo',
      key: 'periodo',
      dataIndex: 'periodo',
      width: '35%',
      align: 'left',
      render: renderPeriodos
    },
    {
      title: 'Acciones',
      key: 'actions',
      dataIndex: 'actions',
      align: 'center',
      width: '15%',
      render: renderActions
    }
  ];

  return (
    <div className="tabla-monitoreo">
      <Modal
        title="Periodos"
        footer={null}
        open={isOpenShowDates}
        onCancel={cancelShowDates}
        width="60em"
        handleCerrar={cancelShowDates}>
        <div style={{ textAlign: 'left' }}>
          {registroSeleccionado != null && (
            <Col>
              <span className="modal-info-sub-desc-result">
                {descProceso} del{' '}
                {construirPeriodo(
                  registroSeleccionado.fechaInicio,
                  registroSeleccionado.fechaFin,
                  false
                )}
                .
              </span>
              <div className="periodos-semanas-70" style={{ marginTop: '40px' }}>
                <Col>
                  <ul>
                    {registroSeleccionado.fechas.map((date, index) => (
                      <div className="semana" key={`${date.fechaInicio}-${date.fechaFin}`}>
                        <span className={`desc desc-${index}`}>
                          {construirPeriodo(date.fechaInicio, date.fechaFin, true)}
                        </span>
                      </div>
                    ))}
                  </ul>
                </Col>
              </div>
            </Col>
          )}
        </div>
      </Modal>
      <Modal
        open={isOpenEliminar}
        onCancel={cancelDelete}
        width="30em"
        cancelButtonProps={{ style: { marginRigth: '4em' } }}
        okButtonProps={{ style: { marginLeft: '4em' } }}
        okText="Aceptar"
        onOk={confirmDelete}
        handleCerrar={cancelDelete}>
        {registroSeleccionado != null && (
          <div style={{ textAlign: 'center', marginTop: '1.5em', fontSize: '1.2em' }}>
            ¿Deseas eliminar el periodo del <br />
            {construirPeriodoCorto(registroSeleccionado.fechaInicio)} al{' '}
            {construirPeriodoCorto(registroSeleccionado.fechaFin)}?
          </div>
        )}
      </Modal>
      <Modal
        width="30em"
        onCancel={() => setIsOpenEliminarNoPermitido(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
        open={isOpenEliminarNoPermitido}
        okText="Aceptar"
        onOk={() => setIsOpenEliminarNoPermitido(false)}>
        <div style={{ textAlign: 'center', marginTop: '1.5em', fontSize: '1.2em' }}>
          Existen monitoreos con periodos de fechas de este registro y no es posible eliminarlo.
        </div>
      </Modal>
      <Modal
        width="35em"
        open={confirmEliminar}
        title=""
        footer={null}
        onCancel={handleCerrarSuccess}>
        <div>
          <div style={{ marginTop: '30px', display: 'grid', justifyContent: 'center' }}>
            <img
              alt="example"
              style={{ width: '180px', marginLeft: '70px' }}
              src={confirmarEliminar}
            />
            <span className="modal-eliminar">Periodo eliminado exitosamente.</span>
          </div>
        </div>
      </Modal>
      <ModalFormCatFechas
        visible={isFormModalVisible}
        initialData={registroSeleccionado}
        periodosProceso={periodosRegistrados}
        onClose={handleCloseModal}
      />
      <div className="table-header">
        <div className="footer-page-pagination">
          <SelectTamanoPagina
            value={pagination.pageSize}
            onChange={handleChangePageSize}
            disableBottomExcel={downloadingExcel}
            onClick={handleDownloadExcel}
          />
        </div>{' '}
        <Button text="Agregar periodo" type="primary" onClick={handleOpenModal} />
      </div>
      {/* {contextHolder} */}
      <Table
        bordered={false}
        columns={columns}
        dataSource={dataList}
        loading={loading}
        rowKey={generateId()}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize || prev.pageSize
            }));
          }
        }}
      />
    </div>
  );
};

export default TablaCatalogo;

TablaCatalogo.propTypes = {
  mainFilters: PropTypes.shape({
    fechaInicio: PropTypes.shape({}),
    fechaFin: PropTypes.shape({}),
    idProcesoGral: PropTypes.number
  }).isRequired,
  seleccion: PropTypes.shape({
    current: PropTypes.shape({})
  }).isRequired
};
